import { Calendar, MapPin, Clock, Users, Download, Check, X, HelpCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { CalendarEvent } from '@/types/email';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { respondToCalendarInvitation } from '@/api/emails';
import { getErrorMessage } from '@/lib/error';
import { useAccountStore } from '@/stores/accountStore';

interface CalendarInvitationProps {
  calendarEvent: CalendarEvent;
  messageId: string;
  accountId: string;
  className?: string;
  onCalendarEventUpdate?: (calendarEvent: CalendarEvent) => void;
}

export function CalendarInvitation({
  calendarEvent,
  messageId,
  accountId,
  className,
  onCalendarEventUpdate,
}: CalendarInvitationProps) {
  const [eventState, setEventState] = useState(calendarEvent);
  const [responding, setResponding] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);

  useEffect(() => {
    setEventState(calendarEvent);
  }, [calendarEvent]);

  const account = useAccountStore(
    (state) => state.accounts.find((acct) => acct.id === accountId) ?? null,
  );

  const getStatusIcon = (partstat: string | null | undefined) => {
    const status = partstat?.toUpperCase();
    if (status === 'ACCEPTED') {
      return <Check className="h-3 w-3 text-success-ink" />;
    }
    if (status === 'DECLINED') {
      return <X className="h-3 w-3 text-danger-ink" />;
    }
    if (status === 'TENTATIVE') {
      return <HelpCircle className="h-3 w-3 text-warning-ink" />;
    }
    return <Clock className="h-3 w-3 text-muted-foreground" />;
  };

  const getStatusLabel = (partstat: string | null | undefined) => {
    const status = partstat?.toUpperCase();
    if (status === 'ACCEPTED') return 'Accepted';
    if (status === 'DECLINED') return 'Declined';
    if (status === 'TENTATIVE') return 'Maybe';
    if (status === 'NEEDS-ACTION') return 'Awaiting response';
    return 'No response';
  };

  const responderEmail = useMemo(() => {
    if (!account) {
      return null;
    }
    return account.emailAddress ?? null;
  }, [account]);

  const currentResponse = useMemo(() => {
    if (eventState.responseStatus) {
      return eventState.responseStatus;
    }
    if (!responderEmail) {
      return null;
    }
    const match = eventState.attendees?.find(
      (attendee) => attendee.email?.toLowerCase() === responderEmail.toLowerCase(),
    );
    return match?.partstat ?? null;
  }, [eventState.attendees, eventState.responseStatus, responderEmail]);

  const handleResponse = async (response: 'ACCEPTED' | 'DECLINED' | 'TENTATIVE') => {
    setResponding(true);
    setResponseError(null);
    try {
      const result = await respondToCalendarInvitation(accountId, messageId, response);
      if (result.calendarEvent) {
        setEventState(result.calendarEvent);
        onCalendarEventUpdate?.(result.calendarEvent);
      }
    } catch (error) {
      setResponseError(getErrorMessage(error) ?? 'Failed to send response');
    } finally {
      setResponding(false);
    }
  };

  const handleDownloadICS = () => {
    // Create a blob from the raw ICS content and download it
    const rawIcs = (eventState as { rawIcs?: string }).rawIcs;
    if (!rawIcs) return;
    const blob = new Blob([rawIcs], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${eventState.summary || 'event'}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });
      return { date: dateStr, time: timeStr };
    } catch {
      return null;
    }
  };

  // Use startDateTime/endDateTime from schema, with fallback to start/end for backwards compat
  const startValue = eventState.startDateTime ?? (eventState as { start?: string }).start ?? null;
  const endValue = eventState.endDateTime ?? (eventState as { end?: string }).end ?? null;
  const startDateTime = startValue ? formatDateTime(startValue) : null;
  const endDateTime = endValue ? formatDateTime(endValue) : null;

  const formatTimeRange = () => {
    if (!startDateTime || !endDateTime) return null;

    // If same day, show time range
    if (startDateTime.date === endDateTime.date) {
      return {
        date: startDateTime.date,
        time: `${startDateTime.time.split(' ')[0]} - ${endDateTime.time}`,
      };
    }

    // Different days
    return {
      date: startDateTime.date,
      time: startDateTime.time,
      endDate: endDateTime.date,
      endTime: endDateTime.time,
    };
  };

  const timeRange = formatTimeRange();

  // iTIP METHOD decides what the card *is*: CANCEL means the meeting is off;
  // REPLY is someone answering an invite (usually one we organized).
  // Everything else (REQUEST, and also PUBLISH — booking systems send
  // RSVP-able invites with it) keeps the RSVP buttons.
  const method = (eventState.method ?? 'REQUEST').toUpperCase();
  const isCancelled = method === 'CANCEL' || eventState.status?.toUpperCase() === 'CANCELLED';
  const isReply = method === 'REPLY';
  const canRespond = !isCancelled && !isReply;

  return (
    <TooltipProvider>
      <div
        className={cn(
          'glass-surface space-y-4 rounded-xl border-2 border-primary/20 bg-primary/5 p-4',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 rounded-lg bg-primary/10 p-2">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <h3
                className={cn(
                  'text-base font-semibold text-foreground',
                  isCancelled && 'line-through decoration-danger-ink/60',
                )}
              >
                {eventState.summary || 'Calendar Invitation'}
              </h3>
              {isCancelled && (
                <p className="text-sm font-medium text-danger-ink">This meeting has been cancelled.</p>
              )}
              {eventState.organizer && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <span>Organized by</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help hover:text-foreground transition-colors">
                        {eventState.organizer.name || eventState.organizer.email}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{eventState.organizer.email}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownloadICS}
            className="flex-shrink-0"
            title="Download ICS file"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          {timeRange && (
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 text-sm">
                <div className="font-medium text-foreground">{timeRange.date}</div>
                <div className="text-muted-foreground">{timeRange.time}</div>
                {timeRange.endDate && timeRange.endTime && (
                  <div className="mt-1 text-muted-foreground">
                    Until {timeRange.endDate} at {timeRange.endTime}
                  </div>
                )}
              </div>
            </div>
          )}

          {eventState.location && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 text-sm text-foreground">{eventState.location}</div>
            </div>
          )}

          {eventState.attendees && eventState.attendees.length > 0 && (
            <div className="flex items-start gap-3">
              <Users className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 text-sm">
                <div className="text-foreground font-medium mb-2">
                  {eventState.attendees.length} {eventState.attendees.length === 1 ? 'Attendee' : 'Attendees'}
                </div>
                <div className="space-y-1">
                  {eventState.attendees.map((attendee, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-1.5">
                            {getStatusIcon(attendee.partstat)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{getStatusLabel(attendee.partstat)}</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-muted-foreground hover:text-foreground transition-colors">
                            {attendee.name || attendee.email}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{attendee.email}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {eventState.description && eventState.description !== eventState.summary && (
            <div className="border-t border-border/50 pt-3">
              <p className="whitespace-pre-line text-sm text-muted-foreground">
                {eventState.description}
              </p>
            </div>
          )}
        </div>

        {(canRespond || currentResponse || isReply) && (
        <div className="border-t border-border/50 pt-3 space-y-2">
          {isReply && (
            <p className="text-xs text-muted-foreground">
              This is a response to your invitation — see the attendee list above.
            </p>
          )}
          {!isReply && currentResponse && (
            <p className="text-xs text-muted-foreground">
              Your response: <span className="font-medium">{getStatusLabel(currentResponse)}</span>
            </p>
          )}
          {canRespond && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleResponse('ACCEPTED')}
              disabled={responding}
              variant="default"
              size="sm"
              className="flex items-center gap-1.5"
              aria-pressed={currentResponse?.toUpperCase() === 'ACCEPTED'}
            >
              <Check className="h-4 w-4" />
              Accept
            </Button>
            <Button
              onClick={() => handleResponse('TENTATIVE')}
              disabled={responding}
              variant={currentResponse?.toUpperCase() === 'TENTATIVE' ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-1.5"
              aria-pressed={currentResponse?.toUpperCase() === 'TENTATIVE'}
            >
              <HelpCircle className="h-4 w-4" />
              Maybe
            </Button>
            <Button
              onClick={() => handleResponse('DECLINED')}
              disabled={responding}
              variant={currentResponse?.toUpperCase() === 'DECLINED' ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-1.5"
              aria-pressed={currentResponse?.toUpperCase() === 'DECLINED'}
            >
              <X className="h-4 w-4" />
              Decline
            </Button>
          </div>
          )}
          {responseError && (
            <p className="text-xs text-destructive">{responseError}</p>
          )}
        </div>
        )}
      </div>
    </TooltipProvider>
  );
}
